import appConfig from '@config/app';
import { AuthenticationService } from '@app/services';
import { ErrorMessage } from '@app/models/api';

interface GenerateHeadersParameters {
	accessToken?: string;
	headerKVPs?: { [key: string]: string };
}

// Every production controller will be inheriting from this class.
export default class ApiController {

	protected readonly baseUrl: string = `${appConfig.application_api_domain}/api`;

	/**
	 * Makes GET call to the backend api.
	 * @param url
	 * @param headers
	 * @param parameters
	 */
	protected async makeGetCall(url: string,
		headers?: { [key: string]: string },
		parameters?: { [key: string]: string },
		withAuthentication: boolean = true): Promise<Response> {

		return new Promise<Response>((resolve, reject) => {
			const buildRequest = (accessToken: string | undefined = undefined): void => {
				// Initialize the GET request.
				const request = new Request(this.generateUrl(url, parameters), {
					headers: new Headers(this.generateHeaders({
						accessToken: accessToken,
						headerKVPs: headers, 
					})),
					method: 'GET'
				});

				resolve(fetch(request)
					.then(this.handleResponse)
					.catch(this.handleError));
			}

			const handleFailedAuthentication = (): void => {
				reject(new ErrorMessage(401, 'User is not authenticated', ''));
			}

			//Refresh the access token before making an API call.
			//This will ensure that if the token has expired and the user is still logged in,
			//we can just refresh the token without having the user to authenticate again.
			//If the token has not expired yet when we make an API call,
			//it will grab it from session storage.
			if (withAuthentication) {
				AuthenticationService.getInstance().getAccessTokenWithRedirect({
					getTokenSuccessCallback: buildRequest,
					getTokenFailCallback: handleFailedAuthentication,
				});
			}
			else {
				buildRequest();
			}
		});
	}

	/**
	 * Makes POST call to the backend api.
	 * @param url
	 * @param data
	 * @param headers
	 */
	protected async makePostCall(url: string,
		data: any,
		headers?: { [key: string]: string },
		parameters?: { [key: string]: string },
		withAuthentication: boolean = true): Promise<Response> {

		return new Promise<Response>((resolve, reject) => {
			const buildRequest = (accessToken: string | undefined = undefined): void => {
				// Initialize the POST request
				const request = new Request(this.generateUrl(url, parameters), {
					headers: this.generateHeaders({
						accessToken: accessToken,
						headerKVPs: headers,
					}),
					method: 'POST',
					body: JSON.stringify(data)
				});

				resolve(fetch(request)
					.then(this.handleResponse)
					.catch(this.handleError));
			}

			const handleFailedAuthentication = (): void => {
				reject(new ErrorMessage(401, 'User is not authenticated', ''));
			}

			//Refresh the access token before making an API call.
			//This will ensure that if the token has expired and the user is still logged in,
			//we can just refresh the token without having the user to authenticate again.
			//If the token has not expired yet when we make an API call,
			//it will grab it from session storage.
			if (withAuthentication) {
				AuthenticationService.getInstance().getAccessTokenWithRedirect({
					getTokenSuccessCallback: buildRequest,
					getTokenFailCallback: handleFailedAuthentication,
				});
			}
			else {
				buildRequest();
			}
		});
	}

	/**
	 * Makes PUT call to the backend api.
	 * @param url
	 * @param data
	 * @param headers
	 */
	protected async makePutCall(url: string,
		data: any,
		headers?: { [key: string]: string },
		parameters?: { [key: string]: string },
		withAuthentication: boolean = true): Promise<Response> {

		return new Promise<Response>((resolve, reject) => {
			const buildRequest = (accessToken: string | undefined = undefined): void => {
				// Initialize the PUT request
				const request = new Request(this.generateUrl(url, parameters), {
					headers: this.generateHeaders({
						accessToken: accessToken,
						headerKVPs: headers,
					}),
					method: 'PUT',
					body: JSON.stringify(data)
				});

				resolve(fetch(request)
					.then(this.handleResponse)
					.catch(this.handleError));
			}

			const handleFailedAuthentication = (): void => {
				reject(new ErrorMessage(401, 'User is not authenticated', ''));
			}

			//Refresh the access token before making an API call.
			//This will ensure that if the token has expired and the user is still logged in,
			//we can just refresh the token without having the user to authenticate again.
			//If the token has not expired yet when we make an API call,
			//it will grab it from session storage.
			if (withAuthentication) {
				AuthenticationService.getInstance().getAccessTokenWithRedirect({
					getTokenSuccessCallback: buildRequest,
					getTokenFailCallback: handleFailedAuthentication,
				});
			}
			else {
				buildRequest();
			}
		});
	}

	/**
	 * Makes DELETE call to the backend api.
	 * @param url
	 * @param headers
	 * @param parameters
	 */
	protected async makeDeleteCall(url: string,
		headers?: { [key: string]: string },
		parameters?: { [key: string]: string },
		withAuthentication: boolean = true,
		data: any | undefined = undefined): Promise<Response> {

		return new Promise<Response>((resolve, reject) => {
			const buildRequest = (accessToken: string | undefined = undefined): void => {
				// Initialize the DELETE request
				const request = new Request(this.generateUrl(url, parameters), {
					headers: this.generateHeaders({
						accessToken: accessToken,
						headerKVPs: headers,
					}),
					method: 'DELETE',
					body: data !== undefined ? JSON.stringify(data) : undefined,
				});
				resolve(fetch(request)
					.then(this.handleResponse)
					.catch(this.handleError));
			};

			const handleFailedAuthentication = (): void => {
				reject(new ErrorMessage(401, 'User is not authenticated', ''));
			}

			//Refresh the access token before making an API call.
			//This will ensure that if the token has expired and the user is still logged in,
			//we can just refresh the token without having the user to authenticate again.
			//If the token has not expired yet when we make an API call,
			//it will grab it from session storage.
			if (withAuthentication) {
				AuthenticationService.getInstance().getAccessTokenWithRedirect({
					getTokenSuccessCallback: buildRequest,
					getTokenFailCallback: handleFailedAuthentication,
				});
			}
			else {
				buildRequest();
			}
		});
	}

	/**
	 * Generates the default header with any additional headers.
	 * @param headerKVPs
	 */
	private generateHeaders(params: GenerateHeadersParameters): Headers {
		// Initialize the headers for request.
		// The access token will be embedded here.
		let headers: { [key: string]: string } = {
			'Content-Type': 'application/json',
			'Pragma': 'no-cache'
		};

		if (!String.isNullOrWhiteSpace(params.accessToken)) {
			headers['Authorization'] = `Bearer ${params.accessToken}`;

			// Add a request header identifying a user to impersonate in the API calls, if one is flagged to be in our local storage.
			let impersonateUserId: number | null = AuthenticationService.getInstance().getImpersonateUserId();
			if (impersonateUserId != null) {
				headers['ImpersonateUserId'] = impersonateUserId.toString();
			}
		}

		// Add any additional headers that were passed in.
		const headerKVPs: { [key: string]: string } | undefined = params.headerKVPs;
		if (headerKVPs !== undefined) {
			Object.keys(headerKVPs).forEach(key => headers[key] = headerKVPs[key]);
		}

		return new Headers(headers);
	}

	/**
	 * Generates url with any possible parameters.
	 * @param url
	 * @param parameters
	 */
	private generateUrl(url: string, parameters?: { [key: string]: string }): string {
		// Set up request url.
		let urlRequest: string = this.baseUrl + '/' + url;

		// Add any parameters that were passed in.
		if (parameters !== null && parameters !== undefined) {
			urlRequest += '?';
			let params: string[] = [];
			Object.keys(parameters).forEach(key => params.push(key + '=' + encodeURIComponent(parameters[key])));
			urlRequest += params.join('&');
		}

		// Always encode the url.
		return urlRequest;
	}

	private handleResponse = (response: Response) => {
		if (!response.ok) {
			throw response;
		}
		return response;
	}

	private instanceOfResponse(objToCheck: any): objToCheck is Response {
		return (objToCheck as Response).status !== undefined;
	}

	private handleError = (error: Response | TypeError) => {
		if (this.instanceOfResponse(error)) {
			if (error.status === 401) {
				//If the request comes back as 'UNAUTHORIZED' we redirect to login page.
				AuthenticationService.getInstance().login();
			}

			return error.text().then((message) => {
				throw new ErrorMessage(error.status, message, '');
			});
		}

		throw new ErrorMessage(500, error.message, '');
	}
}